import React, { useState, useEffect, useRef } from 'react'

import _, { isNull } from 'lodash'
import { LookupField } from '../lookup/LookupField'

const DEFAULT_FILTER_DEBOUNCE_MS = 400

export default function LtsLookup(props) {
  const [records, setRecords] = useState([])
  const [count, setCount] = useState(0)
  const [rows, setRows] = useState(15)
  const [index, setIndex] = useState(0)
  const [sortOrder, setSortOrder] = useState(0)
  const [sortField, setSortField] = useState('')
  const [visible, setVisible] = useState(false)
  const [searchTrigger, setSearchTrigger] = useState(0)

  const rowsRef = useRef(rows)
  rowsRef.current = rows

  const filtersRef = useRef(props.filters)
  filtersRef.current = props.filters

  const filterDebounceTimerRef = useRef(null)
  const dialogWasOpenRef = useRef(false)
  const lastFiltersKeyRef = useRef('')

  const clearFilterDebounce = () => {
    if (filterDebounceTimerRef.current != null) {
      clearTimeout(filterDebounceTimerRef.current)
      filterDebounceTimerRef.current = null
    }
  }

  useEffect(() => {
    if (searchTrigger > 0) {
      loadData({ index, rows })
    }
  }, [searchTrigger])

  const filtersKey = () => JSON.stringify(props.filters || [])

  useEffect(() => {
    if (!visible) {
      clearFilterDebounce()
      dialogWasOpenRef.current = false
      lastFiltersKeyRef.current = ''
      return
    }

    const debounceMs = _.defaultTo(props.filterDebounceMs, DEFAULT_FILTER_DEBOUNCE_MS)
    const justOpened = !dialogWasOpenRef.current
    dialogWasOpenRef.current = true

    const key = filtersKey()

    if (justOpened) {
      clearFilterDebounce()
      lastFiltersKeyRef.current = key
      loadData({ index: 0, rows: rowsRef.current })
      return
    }

    if (key === lastFiltersKeyRef.current) {
      return
    }
    lastFiltersKeyRef.current = key

    clearFilterDebounce()
    filterDebounceTimerRef.current = setTimeout(() => {
      filterDebounceTimerRef.current = null
      loadData({ index: 0, rows: rowsRef.current })
    }, debounceMs)

    return clearFilterDebounce
  }, [visible, props.filters])

  const loadData = ({ index = 0, rows = 15 }) => {
    let sortFields = []
    if (sortField) {
      sortFields.push({ fieldName: sortField, order: sortOrder === 1 ? 'ASC' : 'DESC' })
    }
    let projectionFieldsColumns = props.columns.map((col) => col.columnKey)
    const extras = _.get(props, 'extraProjectionFields', null)
    if (extras && extras.length) {
      projectionFieldsColumns = _.union(projectionFieldsColumns, extras)
    }
    const addFiltroDistint = props.addFiltroDistint

    const filters = [...(filtersRef.current || [])].filter((f) => !isNull(f))
    const params = {
      index,
      addFiltroDistint: addFiltroDistint,
      max: rows,
      filters: filters,
      projectionFields: projectionFieldsColumns,
      sortFields,
    }

    params.filters.push({ campo: 'ativo', valor: true, tipoFiltro: 'IGUAL_A' })
    const resolveFiltersFn = _.get(props, 'resolveFilters', null)
    if (resolveFiltersFn) {
      resolveFiltersFn.call(resolveFiltersFn, params.filters)
    }

    const loadDataFn = _.get(props, 'loadDataFn', null)
    if (loadDataFn) {
      loadDataFn.call(loadDataFn, params).then(onSuccessLoadData).catch(onErrorLoadData)
    }
  }

  const onSuccessLoadData = (response) => {
    const data = _.get(response, 'data', {})
    const records_ = _.get(data, 'records', [])
    const page_ = _.get(data, 'page', 1)
    const index_ = _.get(data, 'index', 0)
    const rows_ = _.get(data, 'size', 15)
    const count_ = _.get(data, 'count', 0)
    setRecords(records_)
    setCount(count_)
    setRows(rows_)
    setIndex(index_)
  }

  const onErrorLoadData = (error) => {
    setRecords([])
    console.log(error)
  }

  const onHide = (e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    setVisible(false)
    setRecords([])
    setCount(0)
    setRows(15)
    setIndex(0)
  }

  const onCancel = (e) => {
    onHide(e)
  }

  const onConfirm = (e) => {
    props.onChange(e.selection)
    onHide(e.originalEvent)
  }

  const onPage = (e) => {
    loadData({
      index: _.get(e, 'first', 0),
      rows: _.get(e, 'rows', 15),
    })
  }

  const onSort = (e) => {
    setSortField(e.sortField)
    setSortOrder(e.sortOrder)
    setSearchTrigger((prev) => prev + 1)
  }

  const onClick = (e) => {
    if (props.onClick) {
      props.onClick(e)
    } else {
      e.preventDefault()
      e.stopPropagation()
      props.setFilters([])
      setVisible(true)
    }
  }

  const onCompleteWrapper = (event) => {
    if (props.onComplete) {
      props.onComplete(event)
    }
    setSearchTrigger((prev) => prev + 1)
  }

  const onEdit = () => {
    const path = _.get(props, 'pathOnEdit', null)
    if (path) {
      return `${path}/`
    }
    const pathOnEditFn = _.get(props, 'pathOnEditFn', null)
    if (pathOnEditFn) {
      return pathOnEditFn.call(pathOnEditFn)
    }
  }

  const onFilter = (e) => {
    const filters = []
    const keys = Object.keys(e.filters) || []
    keys.forEach((key) => {
      const value = e.filters[key].value
      if (value) {
        let filter = {
          campo: key,
          valor: value,
          tipoFiltro: 'CONTEM',
        }
        filters.push(filter)
      }
    })
    props.setFilters(filters)
  }

  const onKeyPressWrapper = (e) => {
    if (props.onKeyPress) {
      props.onKeyPress(e)
    }
    setSearchTrigger((prev) => prev + 1)
  }

  return (
    <React.Fragment>
      <LookupField
        label={props.label}
        value={props.value}
        name={props.name}
        field={props.field}
        history={props.history}
        itemTemplate={props.autoCompleteTemplate}
        onKeyPress={onKeyPressWrapper}
        onComplete={onCompleteWrapper}
        onInputFilter={props.onInputFilter}
        editDisabled={props.editDisabled}
        editHidden={props.editHidden}
        onEditField={onEdit}
        onClick={onClick}
        visible={visible}
        modal={true}
        header={props.header}
        onHide={onHide}
        onConfirm={onConfirm}
        onCancel={onCancel}
        records={records}
        columns={props.onResolvingColumn()}
        totalRecords={count}
        order={sortOrder}
        sortField={sortField}
        first={index}
        rows={rows}
        onPage={onPage}
        onFilter={onFilter}
        onSort={onSort}
        showAddButton={props.showAddButton} //exibir botão '+'
        onClickAddButton={props.onClickAddButton} //
        showWhatsAppButton={props.showWhatsAppButton} //
        onClickWhatsAppButton={props.onClickWhatsAppButton} //
        multiple={props.multiple} //
        disabled={props.disabled} //
        dialogWidth={props.dialogWidth} //
        showFiltroTemporaria={props.showFiltroTemporaria}
        onChangeTemporaria={props.onChangeTemporaria}
        apenasTemporaria={props.apenasTemporaria}
        customEditFn={props.customEditFn}
        noSuggestions={props.noSuggestions}
        unidade={props.unidade}
        unidades={props.unidades}
        showFiltroUnidade={props.showFiltroUnidade}
        onChangeUnit={props.onChangeUnit}
        filtroAdicional={props.filtroAdicional}
      />
    </React.Fragment>
  )
}
